/**
 * API client for jsond.ca
 *
 * Provides both public (unauthenticated) and authenticated fetch wrappers.
 * In dev mode (localhost), returns mock data without hitting the real API.
 */

import { getAuthToken } from './auth.js';
import { MOCK_TAGS, getMockDocuments, getMockDocument, MOCK_QUERY_RESPONSE, MOCK_IDEATION_ITEMS, mockCreateIdeation, MOCK_HYPOTHESES } from './mockData.js';

const API_BASE = 'https://email-loop-api-12364253149.us-central1.run.app';
const DEV_MODE = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function authFetch(path, options = {}) {
  const token = await getAuthToken();
  const res = await fetch(API_BASE + path, {
    ...options,
    headers: {
      ...options.headers,
      'Authorization': 'Bearer ' + token
    }
  });
  if (!res.ok) {
    if (res.status === 401) {
      throw new Error('auth_required');
    }
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || 'Request failed (' + res.status + ')');
  }
  return res.json();
}

// ---------------------------------------------------------------------------
// Public (no auth required)
// ---------------------------------------------------------------------------

export async function fetchDocument(reference) {
  if (DEV_MODE) {
    const doc = getMockDocument(reference);
    if (!doc) throw new Error('not_found');
    return doc;
  }
  try {
    return await authFetch('/api/doc/' + encodeURIComponent(reference));
  } catch (err) {
    if (err.message && err.message.includes('404')) throw new Error('not_found');
    throw err;
  }
}

// ---------------------------------------------------------------------------
// Explorer (auth required)
// ---------------------------------------------------------------------------

export async function fetchExplorerTags() {
  if (DEV_MODE) return MOCK_TAGS;
  return authFetch('/api/explorer/tags');
}

export async function fetchConceptTree() {
  if (DEV_MODE) return { nodes: [] };
  return authFetch('/api/explorer/concept-tree');
}

// Immutable ADRASI system concepts (rubrics, specs, doctrines). Retrieved
// by slug from system_concepts — tenant-agnostic, slug-keyed. Frontend
// uses this list to render the "ADRASI System" schema header at the top
// of the Explorer tree. Content payload (content.markdown) is populated
// by GET /api/system-concepts/:slug; the list endpoint returns metadata.
export async function fetchSystemConcepts() {
  if (DEV_MODE) return { system_concepts: [] };
  return authFetch('/api/system-concepts');
}

export async function fetchSystemConcept(slug) {
  if (DEV_MODE) return null;
  return authFetch('/api/system-concepts/' + encodeURIComponent(slug));
}

export async function fetchNodeContributions(nodeId) {
  if (DEV_MODE) return { contributions: [] };
  return authFetch(`/api/explorer/concept-tree/node/${nodeId}/contributions`);
}

export async function trashConceptNodes(nodeIds) {
  if (DEV_MODE) return { trashed: nodeIds.length };
  return authFetch('/api/explorer/concept-tree/trash', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ nodeIds })
  });
}

// ---------------------------------------------------------------------------
// Ideas
// ---------------------------------------------------------------------------

export async function fetchIdeas(params = {}) {
  if (DEV_MODE) return { ideas: MOCK_IDEATION_ITEMS.filter(i => !params.status || i.status === params.status) };
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  const query = qs.toString();
  return authFetch('/api/ideas' + (query ? '?' + query : ''));
}

export async function createIdea(text, origin = 'web') {
  if (DEV_MODE) return mockCreateIdeation(text.slice(0, 60), text, 'draft');
  const data = await authFetch('/api/ideas', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text, origin })
  });
  return data.idea;
}

export async function publishIdea(id) {
  if (DEV_MODE) {
    const item = MOCK_IDEATION_ITEMS.find(i => i.id === id);
    if (item) item.status = 'published';
    return item;
  }
  const data = await authFetch('/api/ideas/' + encodeURIComponent(id) + '/publish', { method: 'POST' });
  return data.idea;
}

export async function unpublishIdea(id) {
  if (DEV_MODE) {
    const item = MOCK_IDEATION_ITEMS.find(i => i.id === id);
    if (item) item.status = 'draft';
    return item;
  }
  const data = await authFetch('/api/ideas/' + encodeURIComponent(id) + '/unpublish', { method: 'POST' });
  return data.idea;
}

export async function updateIdea(id, text) {
  if (DEV_MODE) {
    const item = MOCK_IDEATION_ITEMS.find(i => i.id === id);
    if (item) { item.text = text; item.idea = text; }
    return item;
  }
  const data = await authFetch('/api/ideas/' + encodeURIComponent(id), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text })
  });
  return data.idea;
}

export async function deleteIdea(id) {
  if (DEV_MODE) {
    const idx = MOCK_IDEATION_ITEMS.findIndex(i => i.id === id);
    if (idx >= 0) MOCK_IDEATION_ITEMS.splice(idx, 1);
    return { deleted: true };
  }
  return authFetch('/api/ideas/' + encodeURIComponent(id), { method: 'DELETE' });
}

// ---------------------------------------------------------------------------
// Query (RAG)
// ---------------------------------------------------------------------------

export async function queryConceptTree(question) {
  if (DEV_MODE) return MOCK_QUERY_RESPONSE;
  return authFetch('/api/concept-query', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ question })
  });
}

export async function emailQueryAnswer(question, answer, sources, timing_ms) {
  if (DEV_MODE) return { sent: true };
  return authFetch('/api/query/email', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ question, answer, sources, timing_ms })
  });
}

export async function emailConceptSnapshot({ title, markdown, generality, tier }) {
  if (DEV_MODE) return { sent: true };
  return authFetch('/api/explorer/concept-tree/email', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ title, markdown, generality, tier })
  });
}

// ---------------------------------------------------------------------------
// Source — the capture surface for the block stream
// ---------------------------------------------------------------------------
//
// All endpoints sit under /api/source/* — see backend src/modules/source/.
// Block FSM: pending → fast_indexed → organized (happy path), or
// pending_append → organized, or paused/pruned. The Lapidary digests
// async; Source offers user-controlled shape/pause/delete pre-organization.

export async function fetchSourceBlocks({ since, status, limit = 100 } = {}) {
  const params = new URLSearchParams();
  if (since) params.set('since', since);
  if (status) params.set('status', Array.isArray(status) ? status.join(',') : status);
  if (limit) params.set('limit', String(limit));
  const qs = params.toString();
  return authFetch('/api/source/blocks' + (qs ? '?' + qs : ''));
}

// Phase 2 v1 unified Source view — blocks + documents on one stream.
export async function fetchSourceItems({ page = 1, limit = 100 } = {}) {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('limit', String(limit));
  return authFetch('/api/source/items?' + params.toString());
}

export async function captureSourceBlock({ content, notes, project, attach_to } = {}) {
  return authFetch('/api/source/blocks', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ content, notes, project, attach_to }),
  });
}

export async function getSourceBlock(id) {
  return authFetch('/api/source/blocks/' + encodeURIComponent(id));
}

export async function editSourceBlock(id, { content, notes, attach_to } = {}) {
  const body = {};
  if (content !== undefined) body.content = content;
  if (notes !== undefined) body.notes = notes;
  if (attach_to !== undefined) body.attach_to = attach_to;
  return authFetch('/api/source/blocks/' + encodeURIComponent(id), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function deleteSourceBlock(id) {
  const token = await getAuthToken();
  const res = await fetch(API_BASE + '/api/source/blocks/' + encodeURIComponent(id), {
    method: 'DELETE',
    headers: { 'Authorization': 'Bearer ' + token },
  });
  if (!res.ok && res.status !== 204) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || 'Delete failed (' + res.status + ')');
  }
  return { ok: true };
}

export async function getSourceDigestStatus() {
  return authFetch('/api/source/digest/status');
}

export async function triggerSourceDigest() {
  return authFetch('/api/source/digest/submit', { method: 'POST' });
}

// ---------------------------------------------------------------------------
// Discovery Engine
// ---------------------------------------------------------------------------

export async function fetchDiscoveryRuns(params = {}) {
  if (DEV_MODE) {
    const mockRun = { run_id: 'mock-run-1', run_created_at: new Date(Date.now() - 3600000).toISOString(), run_status: 'completed', fact_count: 166, hypothesis_count: MOCK_HYPOTHESES.length, hypotheses: MOCK_HYPOTHESES };
    return { runs: [mockRun], total_runs: 1, total_hypotheses: MOCK_HYPOTHESES.length };
  }
  const query = params.runLimit ? 'runLimit=' + params.runLimit : '';
  try {
    return await authFetch('/api/discovery/runs' + (query ? '?' + query : ''));
  } catch (err) {
    console.error('fetchDiscoveryRuns failed:', err.message);
    return { runs: [], total_runs: 0, total_hypotheses: 0 };
  }
}

export async function emailHypothesis(id) {
  if (DEV_MODE) return { sent: true, to: 'dev@test.com' };
  return authFetch('/api/discovery/hypotheses/' + encodeURIComponent(id) + '/email', { method: 'POST' });
}

// ---------------------------------------------------------------------------
// Staged upload — files go to GCS immediately, processed in background
// ---------------------------------------------------------------------------

export async function stageUpload(file, { topic, mode } = {}) {
  if (DEV_MODE) return { staged: true, uploadId: 'dev-' + Date.now(), filename: file.name, position: 1 };
  const token = await getAuthToken();
  const form = new FormData();
  form.append('file', file);
  if (topic) form.append('topic', topic);
  if (mode) form.append('mode', mode);
  const res = await fetch(API_BASE + '/api/upload/stage', {
    method: 'POST',
    headers: { 'Authorization': 'Bearer ' + token },
    body: form
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || 'Stage failed (' + res.status + ')');
  }
  return res.json();
}

export async function stageCollection(files) {
  if (DEV_MODE) return { staged: true, uploadId: 'dev-' + Date.now(), fileCount: files.length, position: 1 };
  const token = await getAuthToken();
  const form = new FormData();
  files.forEach(file => form.append('files', file));
  const res = await fetch(API_BASE + '/api/upload/stage-collection', {
    method: 'POST',
    headers: { 'Authorization': 'Bearer ' + token },
    body: form
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || 'Stage collection failed (' + res.status + ')');
  }
  return res.json();
}

export async function getStagedUploads() {
  if (DEV_MODE) return { uploads: [], pending: 0, processing: 0 };
  return authFetch('/api/upload/staged');
}

// ---------------------------------------------------------------------------
// Admin / Settings
// ---------------------------------------------------------------------------

export async function fetchWorkspace() {
  if (DEV_MODE) return { workspace: { id: 'mock', name: 'JSOND', alias: 'jsond', plan_tier: 'team', seat_count: 5, doc_count: 12, topic_count: 3, created_at: '2026-01-15T00:00:00Z' } };
  const data = await authFetch('/api/admin/workspace');
  return data;
}

export async function updateWorkspace(fields) {
  if (DEV_MODE) return { workspace: { ...fields } };
  return authFetch('/api/admin/workspace', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields)
  });
}

export async function fetchMembers() {
  if (DEV_MODE) return { members: [{ id: 'u1', name: 'Mike Gamon', email: 'mgamon1@gmail.com', role: 'admin', joined_at: '2026-01-15T00:00:00Z', last_active_at: new Date().toISOString() }] };
  return authFetch('/api/admin/members');
}

export async function inviteMember(email, role = 'member') {
  if (DEV_MODE) return { member: { id: 'u-new', email, name: email.split('@')[0], role } };
  return authFetch('/api/admin/members/invite', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, role })
  });
}

export async function removeMember(userId) {
  if (DEV_MODE) return { removed: true };
  return authFetch('/api/admin/members/' + encodeURIComponent(userId), {
    method: 'DELETE'
  });
}

export async function purchaseSeats(addSeats) {
  if (DEV_MODE) return { seat_count: 10, added: addSeats };
  return authFetch('/api/admin/seats', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ addSeats })
  });
}

// ---------------------------------------------------------------------------
// Connections
// ---------------------------------------------------------------------------

export async function fetchWebhookUrl() {
  const resp = await authFetch('/api/connections/webhook-url');
  return resp.webhook_url;
}

// ---------------------------------------------------------------------------
// Gardener (harmonization proposals)
// ---------------------------------------------------------------------------

export async function fetchGardenerSummary(tenantId) {
  if (DEV_MODE) return { tenant_id: tenantId, pending_review: 0, approved: 0, applied: 0, auto_applied: 0, rejected: 0 };
  return authFetch('/api/gardener/proposals/summary?tenantId=' + encodeURIComponent(tenantId));
}

export async function fetchGardenerProposals(tenantId, { status = 'pending_review', action, limit = 50 } = {}) {
  if (DEV_MODE) return { tenant_id: tenantId, count: 0, proposals: [] };
  const qs = new URLSearchParams({ tenantId, status, limit: String(limit) });
  if (action) qs.set('action', action);
  return authFetch('/api/gardener/proposals?' + qs.toString());
}

export async function fetchGardenerProposal(tenantId, proposalId) {
  if (DEV_MODE) return null;
  return authFetch('/api/gardener/proposals/' + encodeURIComponent(proposalId) + '?tenantId=' + encodeURIComponent(tenantId));
}

export async function approveGardenerProposal(tenantId, proposalId) {
  if (DEV_MODE) return { proposal_id: proposalId, status: 'applied' };
  return authFetch('/api/gardener/proposals/' + encodeURIComponent(proposalId) + '/approve', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenantId })
  });
}

export async function rejectGardenerProposal(tenantId, proposalId, rejectionReason) {
  if (DEV_MODE) return { proposal_id: proposalId, status: 'rejected', rejection_reason: rejectionReason };
  return authFetch('/api/gardener/proposals/' + encodeURIComponent(proposalId) + '/reject', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenantId, rejectionReason })
  });
}

export async function fetchGardenerTargets(tenantId, { mode = 'metadata', limit = 20 } = {}) {
  if (DEV_MODE) return { tenant_id: tenantId, mode, has_harmonization_work: false };
  return authFetch('/api/mind/gardener-targets', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenantId, mode, limit })
  });
}

export async function runGardenerFlashSweep(tenantId, { candidateLimit = 3 } = {}) {
  if (DEV_MODE) return { tenant_id: tenantId, proposals_queued: 0, candidates_evaluated: 0 };
  return authFetch('/api/gardener/run-flash-sweep', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenantId, candidateLimit })
  });
}

// ---------------------------------------------------------------------------
// User-correction primitive
// ---------------------------------------------------------------------------
//
// Two endpoints today:
//   correctConcept           — commit-time updater. Full-context AI analysis
//                              applies the mutation via content_evolution and
//                              returns provenance. Only AI call on the live path.
//   emailAlterationDetails   — opt-in detailed email of a just-applied alteration.
//
// The earlier live classifier (classifyConcept) was retired 2026-04-28 — it
// added 1.5s of latency on every typing pause for advisory output the user
// doesn't need now that the modal's behaviour dropdown carries their binding
// choice. The dropdown shows static one-line guidance per behaviour instead.

/**
 * Commit-time correction. Returns { pipeline_run_id, primary_node_id,
 * scope_node_ids, operations_applied, provenance, user_chosen_behaviour,
 * system_resolved_to, audit_summary, no_op, total_latency_ms }.
 *
 * `forcedBehaviour` is BINDING: the backend updater obeys the user's chosen
 * behaviour or no_ops cleanly.
 *
 * `conceptIds` is the in-scope set. Length 1 = single-target submit (the
 * normal case); length >1 = cascade submit (chosen target + descendants in
 * one updater call). The first id is the "primary" target.
 *
 * On 4xx errors the response body is { error, code, applied_partial? }; the
 * authFetch helper throws with the error message — caller can inspect
 * `err.message` / `err.code` if needed.
 */
export async function correctConcept({
  conceptIds,
  statementText,
  forcedBehaviour,
  surface = 'modal',
}) {
  if (!Array.isArray(conceptIds) || conceptIds.length === 0) {
    throw new Error('correctConcept: conceptIds must be a non-empty array');
  }
  if (DEV_MODE) {
    const resolved = forcedBehaviour === 'ADD_OR_CORRECT' ? 'CORRECT' : '';
    const ops = conceptIds.map((id, i) => ({
      operation_type: 'refine_concept',
      target_node_id: id,
      target_field: 'context',
      target_subkey: '',
    }));
    return {
      pipeline_run_id: 'dev-mock-' + Math.random().toString(36).slice(2, 10),
      primary_node_id: conceptIds[0],
      scope_node_ids: conceptIds,
      operations_applied: ops,
      provenance: {
        applied_at: new Date().toISOString(),
        surface,
        statement_text: statementText,
        user_chosen_behaviour: forcedBehaviour,
        system_resolved_to: resolved,
        classifier_verdict: null,
        audit_summary: `[mock] Correction applied (dev mode${conceptIds.length > 1 ? `, ${conceptIds.length}-context cascade` : ''}).`,
        operations: ops.map(o => ({ ...o, audit_reason: '[mock] dev-mode reason' })),
      },
      user_chosen_behaviour: forcedBehaviour,
      system_resolved_to: resolved,
      audit_summary: `[mock] Correction applied (dev mode${conceptIds.length > 1 ? `, ${conceptIds.length}-context cascade` : ''}).`,
      no_op: false,
      total_latency_ms: 800,
    };
  }
  return authFetch('/api/concept/correct', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      concept_ids: conceptIds,
      statement_text: statementText,
      forced_behaviour: forcedBehaviour,
      surface,
    }),
  });
}

/**
 * Send the user a full prose breakdown of the just-completed alteration via
 * email. Frontend passes back the structured result it received from
 * `correctConcept` — the backend doesn't look up by pipeline_run_id today.
 *
 * Returns { sent: true } on success.
 */
export async function emailAlterationDetails({
  conceptId,
  conceptName,
  provenance,
  noOp,
  operationsApplied,
  to = null,
}) {
  if (DEV_MODE) {
    return { sent: true };
  }
  return authFetch('/api/concept/correct/email', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      concept_id: conceptId,
      concept_name: conceptName,
      provenance,
      no_op: !!noOp,
      operations_applied: operationsApplied || [],
      to,
    }),
  });
}

// ---------------------------------------------------------------------------
// System Log
// ---------------------------------------------------------------------------

export async function fetchLog(params = {}) {
  if (DEV_MODE) return [];
  const qs = new URLSearchParams();
  if (params.type) qs.set('type', params.type);
  if (params.startDate) qs.set('startDate', params.startDate);
  if (params.endDate) qs.set('endDate', params.endDate);
  const query = qs.toString();
  return authFetch('/api/log' + (query ? '?' + query : ''));
}
